/* eslint-disable unicode-bom */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import AdminLayout from '../components/AdminLayout';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  getAllOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  getOfferById,
  getAllProductsUnpaginated,
  getAllCategories,
  getAllSubcategories
} from '../services/api';
import './Coupon.css';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------
var extractObject = function (res) {
  var d = res && res.data;
  if (!d) return {};
  return d.data || d.offer || d.coupon || d.result || d;
};

var extractArray = function (res) {
  try {
    var d = res && res.data;
    if (!d) return [];
    var arr = [];
    if (Array.isArray(d)) arr = d;
    else if (Array.isArray(d.data)) arr = d.data;
    else if (Array.isArray(d.offers)) arr = d.offers;
    else if (Array.isArray(d.coupons)) arr = d.coupons;
    else if (d.data && Array.isArray(d.data.offers)) arr = d.data.offers;
    else if (d.data && Array.isArray(d.data.coupons)) arr = d.data.coupons;
    else if (Array.isArray(d.result)) arr = d.result;
    return arr.filter(function (item) { return item && item.isDeleted !== true && !item.deletedAt; });
  } catch (e) { console.error('extractArray:', e); return []; }
};

var getId = function (value) {
  if (!value) return '';
  if (typeof value === 'object') {
    var raw = value._id || value.id || value.offerId || value.couponId;
    if (raw) {
      if (typeof raw === 'object') return String(raw.$oid || raw._id || raw.id || (raw.toString ? raw.toString() : ''));
      return String(raw);
    }
    return '';
  }
  return String(value);
};

var getName = function (value) {
  if (!value) return '';
  if (typeof value === 'object') return value.name || value.title || value.label || value._id || value.id || '';
  return String(value);
};

var idsFromValue = function (value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(getId).filter(Boolean);
  var id = getId(value);
  return id ? [id] : [];
};

var labelsFromValue = function (value) {
  if (!value) return '';
  if (Array.isArray(value)) return value.map(getName).filter(Boolean).join(', ');
  return getName(value);
};

var optionFromItem = function (item) {
  var id = getId(item);
  if (!id) return null;
  return { value: id, label: getName(item) || id };
};

var couponTitle = function (coupon) { return (coupon && (coupon.title || coupon.couponTitle)) || ''; };
var couponMinOrder = function (coupon) {
  if (!coupon) return null;
  var v = coupon.minimumOrderAmount !== undefined ? coupon.minimumOrderAmount
        : (coupon.minOrderAmount !== undefined ? coupon.minOrderAmount
        : coupon.minOrder);
  if (v === undefined || v === null || v === '') return null;
  var n = Number(v);
  return isNaN(n) ? null : n;
};
var couponMaxOrder = function (coupon) {
  if (!coupon) return null;
  var v = coupon.maximumOrderAmount !== undefined ? coupon.maximumOrderAmount
        : (coupon.maxOrderAmount !== undefined ? coupon.maxOrderAmount
        : (coupon.maxDiscountAmount !== undefined ? coupon.maxDiscountAmount
        : coupon.maxDiscount));
  if (v === undefined || v === null || v === '') return null;
  var n = Number(v);
  return isNaN(n) ? null : n;
};
var PER_PAGE_OPTIONS = [5, 10, 25, 50];

var resolveApplicableItems = function (coupon, productOptions, categoryOptions, subcategoryOptions) {
  if (!coupon) return { type: 'all', label: 'All Products (Storewide)', items: [] };
  var type = coupon.applicableTo || 'all';

  var getLabels = function (rawList, optionsList) {
    if (!rawList) return [];
    var arr = Array.isArray(rawList) ? rawList : [rawList];
    var results = [];
    arr.forEach(function (val) {
      if (!val) return;
      if (typeof val === 'object') {
        var n = val.name || val.title || val.label;
        if (n) { results.push(String(n)); return; }
      }
      var id = getId(val);
      if (Array.isArray(optionsList)) {
        var matched = optionsList.find(function (opt) {
          return opt && (String(opt.value) === String(id) || String(opt.id) === String(id));
        });
        if (matched && matched.label) { results.push(String(matched.label)); return; }
      }
      results.push(getName(val) || String(id));
    });
    return results;
  };

  if (type === 'products') {
    var rawProds = coupon.products || coupon.product || coupon.applicableProducts || coupon.productIds;
    var prodItems = getLabels(rawProds, productOptions);
    return { type: 'products', label: 'Selected Products', items: prodItems };
  }
  if (type === 'categories') {
    var rawCats = coupon.categories || coupon.category || coupon.applicableCategories;
    var catItems = getLabels(rawCats, categoryOptions);
    return { type: 'categories', label: 'Selected Categories', items: catItems };
  }
  if (type === 'subcategories') {
    var rawSubcats = coupon.subcategories || coupon.subcategory || coupon.applicableSubcategories;
    var subcatItems = getLabels(rawSubcats, subcategoryOptions);
    return { type: 'subcategories', label: 'Selected Subcategories', items: subcatItems };
  }
  if (coupon.products && Array.isArray(coupon.products) && coupon.products.length > 0) {
    var pItems = getLabels(coupon.products, productOptions);
    if (pItems.length > 0) {
      return { type: 'products', label: 'Selected Products', items: pItems };
    }
  }

  return { type: 'all', label: 'All Products (Storewide)', items: [] };
};
var couponStartDate = function (coupon) { return coupon && (coupon.startDate || coupon.startDateTime); };
var couponExpiryDate = function (coupon) { return coupon && (coupon.expiryDate || coupon.endDate || coupon.expiryDateTime); };
var couponUsageCount = function (coupon) {
  if (!coupon) return 0;
  var count = coupon.usedCount !== undefined ? coupon.usedCount : coupon.usageCount;
  var n = Number(count);
  return isNaN(n) ? 0 : n;
};
var couponEnableUsageLimit = function (coupon) {
  if (!coupon) return false;
  if (coupon.enableUsageLimit !== undefined) return !!coupon.enableUsageLimit;
  var limit = couponUsageLimit(coupon);
  return limit !== null && limit !== undefined && limit !== '';
};
var couponUsageLimit = function (coupon) {
  if (!coupon) return null;
  var limit = coupon.usageLimit !== undefined ? coupon.usageLimit : coupon.totalUsageLimit;
  if (limit === undefined || limit === null || limit === '') return null;
  var n = Number(limit);
  return isNaN(n) ? null : n;
};
var couponUsageText = function (coupon) {
  var limit = couponUsageLimit(coupon);
  return couponUsageCount(coupon) + ' / ' + (couponEnableUsageLimit(coupon) && limit !== null ? limit : 'Unlimited');
};
var couponRemainingText = function (coupon) {
  return coupon && coupon.remainingUsage !== undefined && coupon.remainingUsage !== null
    ? coupon.remainingUsage
    : 'Unlimited';
};

var isCouponExpired = function (coupon, now) {
  var dStr = couponExpiryDate(coupon);
  if (!dStr) return false;
  var d = new Date(dStr);
  if (isNaN(d.getTime())) return false;
  now = now || new Date();
  if (typeof dStr === 'string' && dStr.trim().length <= 10) {
    var endOfDay = new Date(dStr.trim() + 'T23:59:59.999');
    if (!isNaN(endOfDay.getTime())) {
      return endOfDay < now;
    }
  }
  return d < now;
};

var getCouponStatus = function (coupon, statusMap) {
  if (!coupon) return 'Inactive';
  var id = getId(coupon);

  // 1. In-memory session status from recent user edit/toggle (if provided)
  if (statusMap && id && statusMap[id]) {
    var override = String(statusMap[id]).toLowerCase();
    if (override === 'active') return 'Active';
    if (override === 'inactive') return 'Inactive';
    if (override === 'expired') return 'Expired';
  }

  var rawStatus = String(coupon.status || '').trim().toLowerCase();

  // 2. Explicit status string from DB / API
  if (rawStatus === 'active') {
    return 'Active';
  }
  if (rawStatus === 'inactive') {
    return 'Inactive';
  }
  if (rawStatus === 'expired') {
    return 'Expired';
  }

  // 3. Date-based expiration check
  var now = new Date();
  if (isCouponExpired(coupon, now)) {
    return 'Expired';
  }

  // 4. Boolean isActive flag fallback
  if (coupon.isActive === true || coupon.isActive === 'true') {
    return 'Active';
  }
  if (coupon.isActive === false || coupon.isActive === 'false') {
    return 'Inactive';
  }

  return 'Active';
};
var statusKey = function (status) { return String(status || '').toLowerCase(); };

var formatDiscount = function (coupon) {
  if (!coupon) return '\u2014';
  var v = coupon.discountValue;
  if (v === undefined || v === null || v === '') return '\u2014';
  return coupon.discountType === 'fixed' ? ('\u20B9' + Number(v).toLocaleString('en-IN')) : (v + '%');
};

var formatCurrency = function (v) {
  if (v === undefined || v === null || v === '') return '\u2014';
  var n = Number(v);
  if (isNaN(n)) return '\u2014';
  return '\u20B9' + n.toLocaleString('en-IN');
};

var formatDateTime = function (v) {
  if (!v) return '\u2014';
  var d = new Date(v);
  if (isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

var toDateInputValue = function (v) {
  if (!v) return '';
  var d = new Date(v);
  if (isNaN(d.getTime())) return '';
  var pad = function (n) { return String(n).padStart(2, '0'); };
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
};

// ---------------------------------------------------------------------------// Icons
// ---------------------------------------------------------------------------
var EyeIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};
var PencilIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};
var TrashIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};
var XIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};
var SearchIcon = function () {
  return React.createElement('svg', { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
    React.createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
};

// -- Custom Select (navy/gold theme, no native browser highlight) -----------
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return o.value === value; });

  return React.createElement('div', { ref: ref, className: 'cpn-select', style: { position: 'relative' } },
    React.createElement('button', {
      type: 'button',
      onClick: function () { setOpen(!open); },
      className: 'cpn-select-trigger' + (open ? ' open' : '')
    },
      selected ? selected.label : 'Select',
      React.createElement('span', { className: 'cpn-select-caret' }, '\u25BC')
    ),
    open && React.createElement('div', { className: 'cpn-select-list' },
      options.map(function (o) {
        var isSel = o.value === value;
        return React.createElement('div', {
          key: o.value,
          onClick: function () { onChange(o.value); setOpen(false); },
          className: 'cpn-select-item' + (isSel ? ' selected' : '')
        }, o.label);
      })
    )
  );
};

var MultiSelect = function (props) {
  var values = Array.isArray(props.values) ? props.values : [];
  var onChange = props.onChange;
  var options = Array.isArray(props.options) ? props.options : [];
  var placeholder = props.placeholder || 'Select items';
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selectedOptions = options.filter(function (o) { return values.indexOf(o.value) !== -1; });
  var label = selectedOptions.length ? selectedOptions.map(function (o) { return o.label; }).join(', ') : placeholder;
  var toggle = function (value) {
    var next = values.indexOf(value) === -1 ? values.concat([value]) : values.filter(function (v) { return v !== value; });
    onChange(next);
  };

  return React.createElement('div', { ref: ref, className: 'cpn-multi', style: { position: 'relative' } },
    React.createElement('button', { type: 'button', onClick: function () { setOpen(!open); }, className: 'cpn-select-trigger' + (open ? ' open' : '') },
      React.createElement('span', { className: selectedOptions.length ? 'cpn-multi-label' : 'cpn-multi-placeholder' }, label),
      React.createElement('span', { className: 'cpn-select-caret' }, '\u25BC')
    ),
    open && React.createElement('div', { className: 'cpn-select-list cpn-multi-list' },
      options.length === 0
        ? React.createElement('div', { className: 'cpn-select-item muted' }, 'No options found')
        : options.map(function (o) {
            var checked = values.indexOf(o.value) !== -1;
            return React.createElement('button', { key: o.value, type: 'button', className: 'cpn-multi-item' + (checked ? ' selected' : ''), onClick: function () { toggle(o.value); } },
              React.createElement('span', { className: 'cpn-check-box' }, checked ? '\u2713' : ''),
              React.createElement('span', null, o.label)
            );
          })
    )
  );
};
var DISCOUNT_TYPE_OPTIONS = [{ value: 'percentage', label: 'Percentage (%)' }, { value: 'fixed', label: 'Fixed Amount (\u20B9)' }];
var APPLICABLE_TO_OPTIONS = [{ value: 'all', label: 'All Products' }, { value: 'products', label: 'Selected Products' }, { value: 'categories', label: 'Selected Categories' }, { value: 'subcategories', label: 'Selected Subcategories' }];
var STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'expired', label: 'Expired' }];
var STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' }
];

var SummaryRow = function (props) {
  var activeFilter = props.activeFilter;
  var onSelectFilter = props.onSelectFilter;

  return React.createElement('div', { className: 'cpn-stat-row' },
    props.cards.map(function (c, i) {
      var isSelected = activeFilter === c.filterValue;
      return React.createElement('div', {
        key: i,
        className: 'cpn-stat-card cpn-stat-card-clickable' + (isSelected ? ' active active-' + c.filterValue : ''),
        onClick: function () {
          if (onSelectFilter) {
            onSelectFilter(c.filterValue);
          }
        },
        role: 'button',
        tabIndex: 0,
        onKeyDown: function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (onSelectFilter) {
              onSelectFilter(c.filterValue);
            }
          }
        }
      },
        React.createElement('div', { className: 'cpn-stat-icon', style: { background: c.iconBg, color: c.iconColor } }, c.icon),
        React.createElement('div', null,
          React.createElement('div', { className: 'cpn-stat-value' }, c.value),
          React.createElement('div', { className: 'cpn-stat-title' }, c.title)
        )
      );
    })
  );
};

var validateCoupon = function (values, editId, allItems) {
  var code = (values.couponCode || '').trim();
  var title = (values.title || '').trim();

  if (!code) return 'Coupon Code is required';
  if (!title) return 'Coupon Title is required';
  if (!values.discountType) return 'Discount Type is required';
  if (values.discountValue === '' || values.discountValue === undefined || values.discountValue === null) return 'Discount Value is required';
  if (!values.startDate) return 'Start Date is required';
  if (!values.expiryDate) return 'Expiry Date is required';
  if (!values.status) return 'Status is required';

  var duplicate = (allItems || []).some(function (c) {
    var cid = c._id || c.id;
    return (c.couponCode || c.code || c.coupon || '').trim().toLowerCase() === code.toLowerCase() && String(cid) !== String(editId);
  });
  if (duplicate) return 'This coupon code already exists; coupon codes must be unique';

  var discountValue = Number(values.discountValue);
  if (isNaN(discountValue) || discountValue <= 0) return 'Discount Value must be greater than 0';
  if (values.discountType === 'percentage' && discountValue > 100) return 'Percentage discount cannot exceed 100%';

  if (values.minimumOrderAmount !== '' && values.minimumOrderAmount !== undefined && values.minimumOrderAmount !== null) {
    var minOrder = Number(values.minimumOrderAmount);
    if (isNaN(minOrder) || minOrder < 0) return 'Minimum Order Amount must be a valid positive number';
  }
  if (values.discountType === 'percentage' && values.maxDiscountAmount !== '' && values.maxDiscountAmount !== undefined && values.maxDiscountAmount !== null) {
    var maxDisc = Number(values.maxDiscountAmount);
    if (isNaN(maxDisc) || maxDisc < 0) return 'Maximum Discount Amount must be a valid positive number';
  }
  if (values.enableUsageLimit) {
    var usageLimit = Number(values.usageLimit);
    if (isNaN(usageLimit) || usageLimit <= 0 || !Number.isInteger(usageLimit)) return 'Usage Limit must be a whole number greater than 0';
  }

  var start = new Date(values.startDate);
  var expiry = new Date(values.expiryDate);
  if (isNaN(start.getTime())) return 'Start Date is invalid';
  if (isNaN(expiry.getTime())) return 'Expiry Date is invalid';
  if (expiry < start) return 'Expiry Date must be on or after the Start Date';

  if (values.applicableTo === 'products' && (!values.products || values.products.length === 0)) return 'Select at least one product';
  if (values.applicableTo === 'categories' && (!values.categories || values.categories.length === 0)) return 'Select at least one category';
  if (values.applicableTo === 'subcategories' && (!values.subcategories || values.subcategories.length === 0)) return 'Select at least one subcategory';

  return null;
};

var EMPTY_FORM = {
  couponCode: '', title: '', description: '', discountType: 'percentage', discountValue: '',
  minimumOrderAmount: '', maxDiscountAmount: '', startDate: '', expiryDate: '',
  enableUsageLimit: false, usageLimit: '', usageCount: 0, applicableTo: 'all',
  products: [], categories: [], subcategories: [], firstOrderOnly: false, status: 'active'
};
// ---------------------------------------------------------------------------
// Luxury Coupon Details Modal
// ---------------------------------------------------------------------------
var TagIcon = function () {
  return React.createElement('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z' }),
    React.createElement('line', { x1: 7, y1: 7, x2: 7.01, y2: 7 })
  );
};

var CopyCheckIcon = function () {
  return React.createElement('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('rect', { x: 9, y: 9, width: 13, height: 13, rx: 2, ry: 2 }),
    React.createElement('path', { d: 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' })
  );
};

var CouponViewDetailsModal = function (props) {
  var coupon = props.coupon;
  var onClose = props.onClose;
  var onEdit = props.onEdit;
  var productOptions = props.productOptions;
  var categoryOptions = props.categoryOptions;
  var subcategoryOptions = props.subcategoryOptions;

  var bCopied = useState(false); var copied = bCopied[0]; var setCopied = bCopied[1];

  useEffect(function () {
    if (!coupon) return;
    var handleKeyDown = function (e) {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return function () { window.removeEventListener('keydown', handleKeyDown); };
  }, [coupon, onClose]);

  if (!coupon) return null;

  var code = coupon.couponCode || coupon.code || coupon.coupon || 'COUPON';
  var status = (props.getStatus ? props.getStatus(coupon) : getCouponStatus(coupon)) || 'Active';
  var sk = statusKey(status);
  var title = couponTitle(coupon) || 'Promotional Offer';
  var discountText = formatDiscount(coupon);
  var minOrder = couponMinOrder(coupon);
  var maxOrder = couponMaxOrder(coupon);
  var maxDiscount = coupon.maxDiscountAmount !== undefined && coupon.maxDiscountAmount !== null && coupon.maxDiscountAmount !== '' ? Number(coupon.maxDiscountAmount) : null;
  var hasMinOrder = minOrder !== null && minOrder !== undefined && minOrder !== '' && !isNaN(Number(minOrder)) && Number(minOrder) > 0;
  var hasMaxOrder = maxOrder !== null && maxOrder !== undefined && maxOrder !== '' && !isNaN(Number(maxOrder)) && Number(maxOrder) > 0;
  var isPercentage = coupon.discountType === 'percentage';
  var startDate = formatDateTime(couponStartDate(coupon));
  var expiryDate = formatDateTime(couponExpiryDate(coupon));
  var usageCount = couponUsageCount(coupon);
  var usageLimit = couponUsageLimit(coupon);
  var hasLimit = couponEnableUsageLimit(coupon) && usageLimit !== null;
  var usagePercent = hasLimit ? Math.min((usageCount / Math.max(usageLimit, 1)) * 100, 100) : 0;
  var remainingText = couponRemainingText(coupon);

  var appScope = resolveApplicableItems(coupon, productOptions, categoryOptions, subcategoryOptions);
  var description = coupon.description && String(coupon.description).trim();

  var copyCode = function () {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(function () {
        setCopied(true);
        setTimeout(function () { setCopied(false); }, 2000);
      });
    }
  };

  var modalContent = React.createElement('div', {
    className: 'cpn-view-overlay',
    onClick: function (e) { if (e.target === e.currentTarget && onClose) onClose(); }
  },
    React.createElement('div', {
      className: 'cpn-view-card',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'cpn-view-modal-title'
    },
      // Header
      React.createElement('div', { className: 'cpn-view-header' },
        React.createElement('div', { className: 'cpn-view-header-left' },
          React.createElement('div', { className: 'cpn-view-icon-badge' },
            React.createElement(TagIcon, null)
          ),
          React.createElement('div', null,
            React.createElement('h3', { id: 'cpn-view-modal-title', className: 'cpn-view-title' }, 'Coupon Details'),
            React.createElement('p', { className: 'cpn-view-subtitle' }, 'Promotional configuration & discount rules')
          )
        ),
        React.createElement('button', {
          type: 'button',
          className: 'cpn-view-close-btn',
          onClick: onClose,
          'aria-label': 'Close'
        }, React.createElement(XIcon, null))
      ),

      // Scrollable Body
      React.createElement('div', { className: 'cpn-view-body' },
        // Hero Voucher Banner
        React.createElement('div', { className: 'cpn-view-hero-voucher' },
          React.createElement('div', { className: 'cpn-view-voucher-top' },
            React.createElement('div', { className: 'cpn-view-code-wrap' },
              React.createElement('span', { className: 'cpn-view-code-label' }, 'CODE'),
              React.createElement('span', { className: 'cpn-view-code-text' }, code),
              React.createElement('button', {
                type: 'button',
                className: 'cpn-view-copy-btn',
                onClick: copyCode,
                title: 'Copy coupon code'
              },
                copied ? '\u2713 Copied' : React.createElement(React.Fragment, null, React.createElement(CopyCheckIcon, null), ' Copy')
              )
            ),
            React.createElement('span', { className: 'cpn-view-status-badge ' + sk },
              React.createElement('span', { className: 'cpn-status-dot' }),
              status.toUpperCase()
            )
          ),

          React.createElement('div', { className: 'cpn-view-voucher-mid' },
            React.createElement('div', { className: 'cpn-view-discount-banner' },
              React.createElement('span', { className: 'cpn-view-discount-val' }, discountText),
              React.createElement('span', { className: 'cpn-view-discount-type' }, isPercentage ? 'OFF CART' : 'FLAT OFF')
            ),
            React.createElement('div', { className: 'cpn-view-voucher-title' }, title),
            coupon.firstOrderOnly && React.createElement('span', { className: 'cpn-first-order-chip' }, '\u2605 First Order Only')
          )
        ),

        // 2x2 Metric Tiles Grid
        React.createElement('div', { className: 'cpn-view-metrics-grid' },
          // Minimum Order Amount
          React.createElement('div', { className: 'cpn-metric-tile' },
            React.createElement('div', { className: 'cpn-metric-label' }, 'MINIMUM ORDER AMOUNT'),
            React.createElement('div', { className: 'cpn-metric-val' }, hasMinOrder ? formatCurrency(minOrder) : 'No Minimum'),
            React.createElement('div', { className: 'cpn-metric-sub' }, hasMinOrder ? 'Minimum cart subtotal required' : 'No minimum order required')
          ),
          // Maximum Order Amount
          React.createElement('div', { className: 'cpn-metric-tile' },
            React.createElement('div', { className: 'cpn-metric-label' }, 'MAXIMUM ORDER AMOUNT'),
            React.createElement('div', { className: 'cpn-metric-val' }, hasMaxOrder ? formatCurrency(maxOrder) : (isPercentage && maxDiscount ? formatCurrency(maxDiscount) : 'No Limit')),
            React.createElement('div', { className: 'cpn-metric-sub' },
              hasMaxOrder
                ? 'Maximum eligible order amount'
                : (isPercentage && maxDiscount ? 'Maximum savings cap' : 'No maximum order limit')
            )
          ),
          // Validity
          React.createElement('div', { className: 'cpn-metric-tile' },
            React.createElement('div', { className: 'cpn-metric-label' }, 'VALIDITY TIMEFRAME'),
            React.createElement('div', { className: 'cpn-metric-val-date' },
              React.createElement('span', null, startDate),
              React.createElement('span', { className: 'cpn-date-arrow' }, '\u2192'),
              React.createElement('span', null, expiryDate)
            ),
            React.createElement('div', { className: 'cpn-metric-sub' }, 'Start to expiry date')
          ),
          // Usage
          React.createElement('div', { className: 'cpn-metric-tile' },
            React.createElement('div', { className: 'cpn-metric-label' }, 'USAGE & REDEMPTIONS'),
            React.createElement('div', { className: 'cpn-metric-val' },
              hasLimit ? (usageCount + ' / ' + usageLimit) : (usageCount + ' used')
            ),
            hasLimit && React.createElement('div', { className: 'cpn-view-progress-wrap' },
              React.createElement('div', { className: 'cpn-view-progress-fill', style: { width: usagePercent + '%' } })
            ),
            React.createElement('div', { className: 'cpn-metric-sub' },
              hasLimit ? (remainingText + ' remaining') : 'Unlimited redemptions'
            )
          )
        ),

        // Scope & Applicable Products Card
        React.createElement('div', { className: 'cpn-view-info-card' },
          React.createElement('div', { className: 'cpn-view-info-row' },
            React.createElement('div', { className: 'cpn-view-info-label' },
              React.createElement('span', { className: 'cpn-info-bullet' }),
              'APPLICABLE TO'
            ),
            React.createElement('div', { className: 'cpn-view-info-value' },
              React.createElement('span', { className: 'cpn-scope-tag' }, appScope.label)
            )
          ),
          appScope.type === 'all'
            ? React.createElement('div', { className: 'cpn-scope-details-box' },
                React.createElement('div', { className: 'cpn-scope-all-text' },
                  React.createElement('span', { className: 'cpn-scope-check-icon' }, '\u2713'),
                  React.createElement('span', null, 'Storewide offer \u2014 Applies to all products in catalog')
                )
              )
            : React.createElement('div', { className: 'cpn-scope-details-box' },
                React.createElement('div', { className: 'cpn-scope-list-hdr' },
                  (appScope.type === 'products' ? 'Applicable Products' : (appScope.type === 'categories' ? 'Applicable Categories' : 'Applicable Subcategories')) + ' (' + appScope.items.length + '):'
                ),
                appScope.items.length > 0
                  ? React.createElement('div', { className: 'cpn-scope-tags-wrap' },
                      appScope.items.map(function (name, idx) {
                        return React.createElement('span', { key: idx, className: 'cpn-scope-product-tag', title: name },
                          React.createElement('span', { className: 'cpn-scope-dot' }, '\u2022'),
                          name
                        );
                      })
                    )
                  : React.createElement('div', { className: 'cpn-scope-empty-text' }, 'No items specified (defaults to storewide)')
              )
        ),

        // Description Card (always visible with clean fallback)
        React.createElement('div', { className: 'cpn-view-desc-card' },
          React.createElement('div', { className: 'cpn-view-desc-hdr' },
            React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              React.createElement('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
              React.createElement('polyline', { points: '14 2 14 8 20 8' }),
              React.createElement('line', { x1: 16, y1: 13, x2: 8, y2: 13 }),
              React.createElement('line', { x1: 16, y1: 17, x2: 8, y2: 17 })
            ),
            'DESCRIPTION'
          ),
          React.createElement('div', { className: 'cpn-view-desc-body' },
            description ? description : React.createElement('span', { className: 'cpn-view-desc-empty' }, 'No description provided for this coupon.')
          )
        )
      ),

      // Footer
      React.createElement('div', { className: 'cpn-view-footer' },
        onEdit && React.createElement('button', {
          type: 'button',
          className: 'cpn-view-btn-edit',
          onClick: function () { onEdit(coupon); }
        },
          React.createElement(PencilIcon, null),
          'Edit Coupon'
        ),
        React.createElement('button', {
          type: 'button',
          className: 'cpn-view-btn-close',
          onClick: onClose
        }, 'Close')
      )
    )
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
var CouponsAdmin = function () {
  var b1 = useState([]); var items = b1[0]; var setItems = b1[1];
  var b2 = useState(true); var loading = b2[0]; var setLoading = b2[1];
  var b3 = useState(false); var submitLoading = b3[0]; var setSubmitLoading = b3[1];
  var b5 = useState(false); var showModal = b5[0]; var setShowModal = b5[1];
  var b6 = useState(null); var editId = b6[0]; var setEditId = b6[1];
  var b7 = useState(null); var viewItem = b7[0]; var setViewItem = b7[1];
  var b8 = useState(null); var pendingDelete = b8[0]; var setPendingDelete = b8[1];
  var b8b = useState(null); var deleteSuccess = b8b[0]; var setDeleteSuccess = b8b[1];
  var b9 = useState({ text: '', type: '' }); var msg = b9[0]; var setMsg = b9[1];
  var b10 = useState(Object.assign({}, EMPTY_FORM)); var form = b10[0]; var setForm = b10[1];
  var b11 = useState(''); var formError = b11[0]; var setFormError = b11[1];
  var b12 = useState(''); var searchQuery = b12[0]; var setSearchQuery = b12[1];
  var b13 = useState('all'); var statusFilter = b13[0]; var setStatusFilter = b13[1];
  var b15 = useState([]); var productOptions = b15[0]; var setProductOptions = b15[1];
  var b16 = useState([]); var categoryOptions = b16[0]; var setCategoryOptions = b16[1];
  var b17 = useState([]); var subcategoryOptions = b17[0]; var setSubcategoryOptions = b17[1];
  var bUS = useState(false); var showUpdateSuccess = bUS[0]; var setShowUpdateSuccess = bUS[1];
  var bPage = useState(1); var currentPage = bPage[0]; var setCurrentPage = bPage[1];
  var bPP = useState(10); var itemsPerPage = bPP[0]; var setItemsPerPage = bPP[1];

  var recentStatusRef = useRef({});
  var getStatus = function (c) { return getCouponStatus(c, recentStatusRef.current); };

  var showMsg = function (text, type) {
    setMsg({ text: text, type: type });
    setTimeout(function () { setMsg({ text: '', type: '' }); }, 5000);
  };

  var setField = function (key, value) {
    setForm(function (prev) {
      var next = Object.assign({}, prev); next[key] = value;
      if (key === 'enableUsageLimit' && !value) next.usageLimit = '';
      if (key === 'applicableTo') {
        next.products = []; next.categories = []; next.subcategories = [];
      }
      return next;
    });
  };

  var resetForm = function () { setForm(Object.assign({}, EMPTY_FORM)); setEditId(null); setFormError(''); };
  var closeModal = function () { setShowModal(false); resetForm(); };

  var fetchData = function () {
    setLoading(true);
    getAllOffers({ _t: Date.now() })
      .then(function (res) {
        var rawList = extractArray(res);
        setItems(function () {
          return rawList.map(function (serverItem) {
            var sid = getId(serverItem);
            var override = sid && recentStatusRef.current && recentStatusRef.current[sid];
            if (override) {
              return Object.assign({}, serverItem, {
                status: override,
                isActive: override === 'active'
              });
            }
            return serverItem;
          });
        });
      })
      .catch(function (err) { showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Failed to load coupons', 'e'); })
      .finally(function () { setLoading(false); });
  };

  var loadPickers = function () {
    Promise.allSettled([getAllProductsUnpaginated(), getAllCategories(), getAllSubcategories()]).then(function (res) {
      if (res[0].status === 'fulfilled') setProductOptions(extractArray(res[0].value).map(optionFromItem).filter(Boolean));
      if (res[1].status === 'fulfilled') setCategoryOptions(extractArray(res[1].value).map(optionFromItem).filter(Boolean));
      if (res[2].status === 'fulfilled') setSubcategoryOptions(extractArray(res[2].value).map(optionFromItem).filter(Boolean));
    });
  };

  useEffect(function () {
    try {
      sessionStorage.removeItem('ishmai_cpn_status_map');
      localStorage.removeItem('ishmai_cpn_status_map');
    } catch (e) {}
    fetchData();
    loadPickers();
  }, []); // eslint-disable-line

  var buildPayload = function (baseItem) {
    var selectedStatus = form.status;
    var isAct = selectedStatus === 'active';
    var payload = Object.assign({}, baseItem || {}, {
      title: form.title.trim(),
      couponCode: form.couponCode.trim().toUpperCase(),
      description: (form.description || '').trim(),
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      minimumOrderAmount: form.minimumOrderAmount !== '' && form.minimumOrderAmount !== null && form.minimumOrderAmount !== undefined ? Number(form.minimumOrderAmount) : null,
      maxDiscountAmount: form.discountType === 'percentage' && form.maxDiscountAmount !== '' && form.maxDiscountAmount !== null && form.maxDiscountAmount !== undefined ? Number(form.maxDiscountAmount) : null,
      maximumOrderAmount: form.maxDiscountAmount !== '' && form.maxDiscountAmount !== null && form.maxDiscountAmount !== undefined ? Number(form.maxDiscountAmount) : null,
      startDate: form.startDate,
      expiryDate: form.expiryDate,
      enableUsageLimit: !!form.enableUsageLimit,
      usageLimit: form.enableUsageLimit ? Number(form.usageLimit) : null,
      applicableTo: form.applicableTo,
      products: form.applicableTo === 'products' ? form.products : [],
      categories: form.applicableTo === 'categories' ? form.categories : [],
      subcategories: form.applicableTo === 'subcategories' ? form.subcategories : [],
      status: selectedStatus,
      isActive: isAct,
      firstOrderOnly: !!form.firstOrderOnly
    });

    delete payload._id;
    delete payload.id;
    delete payload.__v;
    delete payload.createdAt;
    delete payload.updatedAt;
    return payload;
  };

  var handleSubmit = async function () {
    var error = validateCoupon(form, editId, items);
    if (error) { setFormError(error); return; }
    setFormError(''); setSubmitLoading(true);
    try {
      var currentItem = (editId && items.find(function (c) { return String(getId(c)) === String(editId); })) || {};
      var selectedStatus = form.status;
      var isAct = selectedStatus === 'active';
      var payload = buildPayload(currentItem);

      if (editId) {
        recentStatusRef.current[String(editId)] = selectedStatus;
      }

      var res = await (editId ? updateOffer(editId, payload) : createOffer(payload));
      var updatedItem = extractObject(res);

      if (editId) {
        setItems(function (prev) {
          return prev.map(function (c) {
            if (String(getId(c)) === String(editId)) {
              return Object.assign({}, c, payload, updatedItem, {
                status: selectedStatus,
                isActive: isAct
              });
            }
            return c;
          });
        });
      } else {
        var createdId = getId(updatedItem);
        if (createdId) {
          recentStatusRef.current[String(createdId)] = selectedStatus;
        }
      }

      closeModal();
      if (editId) {
        setShowUpdateSuccess(true);
      } else {
        showMsg('Coupon created successfully', 's');
      }
      fetchData();
    } catch (err) {
      showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'Unable to save coupon', 'e');
    } finally { setSubmitLoading(false); }
  };

  var populateForm = function (item) {
    var id = getId(item);
    setEditId(id);
    var derivedStatus = statusKey(getStatus(item));
    if (derivedStatus !== 'active' && derivedStatus !== 'inactive' && derivedStatus !== 'expired') {
      derivedStatus = 'active';
    }

    setForm({
      couponCode: item.couponCode || item.code || item.coupon || '',
      title: couponTitle(item),
      description: item.description || '',
      discountType: item.discountType || 'percentage',
      discountValue: item.discountValue !== undefined && item.discountValue !== null ? String(item.discountValue) : '',
      minimumOrderAmount: couponMinOrder(item) !== undefined && couponMinOrder(item) !== null ? String(couponMinOrder(item)) : '',
      maxDiscountAmount: item.maxDiscountAmount !== undefined && item.maxDiscountAmount !== null ? String(item.maxDiscountAmount) : '',
      startDate: toDateInputValue(couponStartDate(item)),
      expiryDate: toDateInputValue(couponExpiryDate(item)),
      enableUsageLimit: couponEnableUsageLimit(item),
      usageLimit: couponUsageLimit(item) !== undefined && couponUsageLimit(item) !== null ? String(couponUsageLimit(item)) : '',
      usageCount: couponUsageCount(item),
      applicableTo: item.applicableTo || 'all',
      products: idsFromValue(item.products || item.product),
      categories: idsFromValue(item.categories || item.category),
      subcategories: idsFromValue(item.subcategories || item.subcategory),
      firstOrderOnly: !!item.firstOrderOnly,
      status: derivedStatus
    });
  };

  var handleEdit = function (item) {
    var id = getId(item);
    if (!id) { showMsg('Cannot edit: missing ID', 'e'); return; }
    setFormError('');
    populateForm(item);
    setShowModal(true);
    getOfferById(id)
      .then(function (res) {
        var detail = extractObject(res);
        if (detail && typeof detail === 'object') {
          populateForm(Object.assign({}, item, detail));
        }
      })
      .catch(function () {});
  };

  var handleToggleStatus = async function (item, e) {
    if (e) e.stopPropagation();
    var id = getId(item);
    if (!id) return;
    var currentStatus = getStatus(item);
    var isCurrentlyActive = currentStatus === 'Active';
    var nextStatus = isCurrentlyActive ? 'inactive' : 'active';
    var nextIsActive = !isCurrentlyActive;

    recentStatusRef.current[String(id)] = nextStatus;

    // Optimistically update
    setItems(function (prev) {
      return prev.map(function (c) {
        if (String(getId(c)) === String(id)) {
          return Object.assign({}, c, {
            status: nextStatus,
            isActive: nextIsActive
          });
        }
        return c;
      });
    });

    var payload = {
      status: nextStatus,
      isActive: nextIsActive
    };

    try {
      await updateOffer(id, payload);
      showMsg('Coupon status switched to ' + (nextIsActive ? 'Active' : 'Inactive'), 's');
      fetchData();
    } catch (err) {
      showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'Failed to update status', 'e');
      fetchData();
    }
  };

  var handleView = function (item) {
    var id = getId(item);
    if (!id) { setViewItem(item); return; }
    getOfferById(id)
      .then(function (res) {
        var detail = extractObject(res);
        setViewItem(Object.assign({}, item, detail));
      })
      .catch(function () { setViewItem(item); });
  };

  var handleDelete = function (item) {
    var id = getId(item);
    if (!id) { showMsg('Cannot delete: missing ID', 'e'); return; }
    if (recentStatusRef.current) {
      delete recentStatusRef.current[String(id)];
    }
    deleteOffer(id)
      .then(function () {
        setDeleteSuccess({ id: id, code: item.couponCode || item.code || 'Coupon' });
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Delete failed', 'e');
      });
  };

  var handleDeleteSuccessClose = function () {
    var deletedId = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setItems(function (prev) { return prev.filter(function (s) { return String(getId(s)) !== String(deletedId); }); });
    }
    fetchData();
  };

  var totalCount = items.length;
  var activeCount = items.filter(function (c) { return statusKey(getStatus(c)) === 'active'; }).length;
  var inactiveCount = items.filter(function (c) { return statusKey(getStatus(c)) === 'inactive'; }).length;
  var expiredCount = items.filter(function (c) { return statusKey(getStatus(c)) === 'expired'; }).length;

  var filteredItems = items.filter(function (c) {
    var q = searchQuery.trim().toLowerCase();
    var status = statusKey(getStatus(c));
    var matchesSearch = !q || (c.couponCode || c.code || c.coupon || '').toLowerCase().indexOf(q) !== -1 || couponTitle(c).toLowerCase().indexOf(q) !== -1;
    var matchesStatus = statusFilter === 'all' || status === statusKey(statusFilter);
    return matchesSearch && matchesStatus;
  });

  var totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  var safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  var startIndex = (safeCurrentPage - 1) * itemsPerPage;
  var endIndex = Math.min(startIndex + itemsPerPage, filteredItems.length);
  var pageItems = filteredItems.slice(startIndex, endIndex);

  var getPageNumbers = function () {
    var pages = [];
    var start = Math.max(1, safeCurrentPage - 2);
    var end = Math.min(totalPages, safeCurrentPage + 2);
    for (var i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  var statusBadgeClass = function (status) {
    var key = statusKey(status);
    return key === 'active' ? 'bdg bdg-active' : (key === 'expired' ? 'bdg bdg-expired' : 'bdg bdg-inactive');
  };
  var applicableLabel = function (item) {
    var base = (APPLICABLE_TO_OPTIONS.find(function (o) { return o.value === item.applicableTo; }) || {}).label || 'All Products';
    if (item.applicableTo === 'products') return base + (labelsFromValue(item.products || item.product) ? ': ' + labelsFromValue(item.products || item.product) : '');
    if (item.applicableTo === 'categories') return base + (labelsFromValue(item.categories || item.category) ? ': ' + labelsFromValue(item.categories || item.category) : '');
    if (item.applicableTo === 'subcategories') return base + (labelsFromValue(item.subcategories || item.subcategory) ? ': ' + labelsFromValue(item.subcategories || item.subcategory) : '');
    return base;
  };

  var tableTitle = statusFilter === 'all'
    ? 'All Coupons (' + filteredItems.length + ')'
    : (statusFilter === 'active'
        ? 'Active Coupons (' + filteredItems.length + ')'
        : (statusFilter === 'inactive'
            ? 'Inactive Coupons (' + filteredItems.length + ')'
            : (statusFilter === 'expired'
                ? 'Expired Coupons (' + filteredItems.length + ')'
                : 'Coupons (' + filteredItems.length + ')')));

  return React.createElement(AdminLayout, { hideLayoutOnModal: !!(viewItem || showModal || pendingDelete || showUpdateSuccess || deleteSuccess) },
    showUpdateSuccess && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: 'Updated successfully!',
      onClose: function () { setShowUpdateSuccess(false); }
    }),
    viewItem && React.createElement(CouponViewDetailsModal, {
      coupon: viewItem,
      onClose: function () { setViewItem(null); },
      onEdit: function (item) { setViewItem(null); populateForm(item); setShowModal(true); },
      productOptions: productOptions,
      categoryOptions: categoryOptions,
      subcategoryOptions: subcategoryOptions,
      getStatus: getStatus,
      applicableLabel: applicableLabel
    }),

    showModal && React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) closeModal(); } },
      React.createElement('div', { className: 'modal-box cpn-modal-box' },
        React.createElement('div', { className: 'modal-hdr' }, React.createElement('span', { className: 'modal-title' }, editId ? 'Edit Coupon' : 'Add New Coupon'), React.createElement('button', { className: 'modal-close', onClick: closeModal }, React.createElement(XIcon, null))),
        formError && React.createElement('div', { className: 'mbx mbx-e' }, formError),
        React.createElement('div', { className: 'cpn-form-grid' },
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Coupon Code *'), React.createElement('input', { type: 'text', placeholder: 'e.g. ISHMAI10', value: form.couponCode, onChange: function (e) { setField('couponCode', e.target.value.toUpperCase()); }, style: { textTransform: 'uppercase' } })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Coupon Title *'), React.createElement('input', { type: 'text', placeholder: 'e.g. Festival Special', value: form.title, onChange: function (e) { setField('title', e.target.value); } })),
          React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } }, React.createElement('label', null, 'Description'), React.createElement('textarea', { rows: 2, placeholder: 'Short internal note about this coupon', value: form.description, onChange: function (e) { setField('description', e.target.value); } })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Discount Type *'), React.createElement(CustomSelect, { value: form.discountType, onChange: function (v) { setField('discountType', v); }, options: DISCOUNT_TYPE_OPTIONS })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Discount Value * ', React.createElement('span', { className: 'char-count' }, form.discountType === 'percentage' ? '(%)' : '(\u20B9)')), React.createElement('input', { type: 'number', min: '0', placeholder: form.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 250', value: form.discountValue, onChange: function (e) { setField('discountValue', e.target.value); } })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Minimum Order Amount'), React.createElement('input', { type: 'number', min: '0', placeholder: 'e.g. 1000', value: form.minimumOrderAmount, onChange: function (e) { setField('minimumOrderAmount', e.target.value); } })),
          form.discountType === 'percentage' && React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Maximum Discount Amount'), React.createElement('input', { type: 'number', min: '0', placeholder: 'e.g. 300', value: form.maxDiscountAmount, onChange: function (e) { setField('maxDiscountAmount', e.target.value); } })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Start Date *'), React.createElement('input', { type: 'date', value: form.startDate, onChange: function (e) { setField('startDate', e.target.value); } })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Expiry Date *'), React.createElement('input', { type: 'date', value: form.expiryDate, onChange: function (e) { setField('expiryDate', e.target.value); } })),
          React.createElement('label', { className: 'cpn-check-row', style: { gridColumn: '1 / -1' } }, React.createElement('input', { type: 'checkbox', checked: form.enableUsageLimit, onChange: function (e) { setField('enableUsageLimit', e.target.checked); } }), React.createElement('span', null, 'Enable Usage Limit')),
          form.enableUsageLimit && React.createElement('div', { className: 'fl cpn-usage-limit-box', style: { gridColumn: '1 / -1' } },
            React.createElement('label', null, 'Usage Limit'),
            React.createElement('input', { type: 'number', min: '1', step: '1', placeholder: 'e.g. 50', value: form.usageLimit, onChange: function (e) { setField('usageLimit', e.target.value); } }),
            React.createElement('div', { className: 'cpn-usage-progress' }, React.createElement('div', { className: 'cpn-usage-progress-bar', style: { width: Math.min(((Number(form.usageCount) || 0) / Math.max(Number(form.usageLimit) || 1, 1)) * 100, 100) + '%' } })),
            React.createElement('div', { className: 'cpn-usage-summary' },
              React.createElement('div', null, 'Usage: ', Number(form.usageCount) || 0, ' / ', form.usageLimit || '0'),
              React.createElement('div', null, 'Remaining: ', Math.max((Number(form.usageLimit) || 0) - (Number(form.usageCount) || 0), 0))
            )
          ),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Applicable To *'), React.createElement(CustomSelect, { value: form.applicableTo, onChange: function (v) { setField('applicableTo', v); }, options: APPLICABLE_TO_OPTIONS })),
          React.createElement('div', { className: 'fl' }, React.createElement('label', null, 'Status *'), React.createElement(CustomSelect, { value: form.status, onChange: function (v) { setField('status', v); }, options: STATUS_OPTIONS })),
          form.applicableTo === 'products' && React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } }, React.createElement('label', null, 'Products *'), React.createElement(MultiSelect, { values: form.products, onChange: function (v) { setField('products', v); }, options: productOptions, placeholder: 'Select products' })),
          form.applicableTo === 'categories' && React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } }, React.createElement('label', null, 'Categories *'), React.createElement(MultiSelect, { values: form.categories, onChange: function (v) { setField('categories', v); }, options: categoryOptions, placeholder: 'Select categories' })),
          form.applicableTo === 'subcategories' && React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } }, React.createElement('label', null, 'Subcategories *'), React.createElement(MultiSelect, { values: form.subcategories, onChange: function (v) { setField('subcategories', v); }, options: subcategoryOptions, placeholder: 'Select subcategories' })),
          React.createElement('label', { className: 'cpn-check-row', style: { gridColumn: '1 / -1' } }, React.createElement('input', { type: 'checkbox', checked: form.firstOrderOnly, onChange: function (e) { setField('firstOrderOnly', e.target.checked); } }), React.createElement('span', null, 'First order only'))
        ),
        React.createElement('div', { className: 'fa' }, React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: closeModal }, 'Cancel'), React.createElement('button', { className: 'btn btn-p', type: 'button', disabled: submitLoading, style: { opacity: submitLoading ? 0.7 : 1 }, onClick: handleSubmit }, submitLoading ? 'Saving...' : (editId ? 'Update Coupon' : 'Add Coupon')))
      )
    ),

    pendingDelete && React.createElement(DeleteConfirmModal, {
      isOpen: true,
      title: 'Delete Coupon',
      message: 'Are you sure you want to delete this coupon?',
      itemName: pendingDelete.couponCode || pendingDelete.code || 'selected coupon',
      onConfirm: function () { var s = pendingDelete; setPendingDelete(null); handleDelete(s); },
      onCancel: function () { setPendingDelete(null); }
    }),

    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The coupon has been deleted successfully.',
      onClose: handleDeleteSuccessClose
    }),

    msg.text && !showModal && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),
    React.createElement('div', { className: 'page-header-row' }, React.createElement('div', { className: 'page-header-main' }, React.createElement('div', { className: 'page-header-title-row' }, React.createElement('div', { className: 'page-header-icon' }, React.createElement('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M20.59 13.41 12 22l-9-9V3h10l9 9a2 2 0 0 1 0 1.41z' }), React.createElement('circle', { cx: 7.5, cy: 7.5, r: 1.5 }))), React.createElement('div', { className: 'ptitle' }, 'Coupons')), React.createElement('div', { className: 'header-subtitle' }, 'Create and manage discount coupons for the storefront')), React.createElement('button', { className: 'add-btn', type: 'button', onClick: function () { resetForm(); setShowModal(true); } }, React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: 'currentColor' } }, React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })), ' Add Coupon')),
    React.createElement(SummaryRow, {
      activeFilter: statusFilter,
      onSelectFilter: function (filterVal) {
        setStatusFilter(filterVal);
        setCurrentPage(1);
      },
      cards: [
        { filterValue: 'all', title: 'Total', value: totalCount, icon: '\uD83C\uDFF7', iconBg: 'rgba(255,255,255,0.22)', iconColor: '#ffffff' },
        { filterValue: 'active', title: 'Active', value: activeCount, icon: '\u2705', iconBg: 'rgba(255,255,255,0.22)', iconColor: '#ffffff' },
        { filterValue: 'inactive', title: 'Inactive', value: inactiveCount, icon: '\u23F8', iconBg: 'rgba(255,255,255,0.22)', iconColor: '#ffffff' },
        { filterValue: 'expired', title: 'Expired', value: expiredCount, icon: '\u23F3', iconBg: 'rgba(255,255,255,0.22)', iconColor: '#ffffff' }
      ]
    }),
    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'cpn-filter-row' },
        React.createElement('div', { className: 'cpn-search-box' },
          React.createElement(SearchIcon, null),
          React.createElement('input', {
            type: 'text',
            placeholder: 'Search by code or title...',
            value: searchQuery,
            onChange: function (e) {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }
          })
        ),
        React.createElement('div', { className: 'cpn-filter-select' },
          React.createElement(CustomSelect, {
            value: statusFilter,
            onChange: function (v) {
              setStatusFilter(v);
              setCurrentPage(1);
            },
            options: STATUS_FILTER_OPTIONS
          })
        )
      ),
      React.createElement('div', { className: 'wh' }, React.createElement('span', { className: 'wt' }, tableTitle)),
      loading ? React.createElement('div', { className: 'loading-spin' }) : React.createElement(React.Fragment, null,
        React.createElement('div', { style: { overflowX: 'auto' } },
          React.createElement('table', { className: 'tbl' },
            React.createElement('thead', null,
              React.createElement('tr', null,
                React.createElement('th', { style: { width: 54, textAlign: 'center' } }, 'S.NO'),
                React.createElement('th', null, 'Coupon Code'),
                React.createElement('th', null, 'Title'),
                React.createElement('th', null, 'Discount'),
                React.createElement('th', null, 'Usage'),
                React.createElement('th', null, 'Usage Limit'),
                React.createElement('th', null, 'Start Date'),
                React.createElement('th', null, 'Expiry Date'),
                React.createElement('th', null, 'Status'),
                React.createElement('th', { style: { textAlign: 'center' } }, 'Actions')
              )
            ),
            React.createElement('tbody', null,
              pageItems.length === 0
                ? React.createElement('tr', null,
                    React.createElement('td', { colSpan: 10, style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'No coupons found.')
                  )
                : pageItems.map(function (item, idx) {
                    var status = getStatus(item);
                    var sid = item._id || item.id || String(idx);
                    var serialNo = startIndex + idx + 1;
                    return React.createElement('tr', { key: sid },
                      React.createElement('td', { style: { textAlign: 'center', fontWeight: 700, color: 'var(--cpn-navy-800)', fontSize: 12 } }, serialNo),
                      React.createElement('td', { className: 'cpn-code-cell' }, item.couponCode || item.code || item.coupon),
                      React.createElement('td', { style: { fontWeight: 600 } }, couponTitle(item)),
                      React.createElement('td', null, formatDiscount(item)),
                      React.createElement('td', null, React.createElement('div', { className: 'cpn-usage-cell' }, React.createElement('div', null, couponUsageText(item)), React.createElement('div', { className: 'cpn-usage-remaining' }, 'Remaining: ' + couponRemainingText(item)))),
                      React.createElement('td', null, couponUsageLimit(item) === null ? 'Unlimited' : couponUsageLimit(item)),
                      React.createElement('td', { style: { fontSize: 12 } }, formatDateTime(couponStartDate(item))),
                      React.createElement('td', { style: { fontSize: 12 } }, formatDateTime(couponExpiryDate(item))),
                      React.createElement('td', null,
                        React.createElement('span', {
                          className: statusBadgeClass(status) + ' cpn-status-badge-interactive',
                          title: 'Click to switch to ' + ((status === 'Active' || status === 'Scheduled') ? 'Inactive' : 'Active'),
                          onClick: function (e) { handleToggleStatus(item, e); }
                        }, status || '\u2014')
                      ),
                      React.createElement('td', { className: 'actions-cell' },
                        React.createElement('div', { className: 'row-actions' },
                          React.createElement('button', { className: 'row-action-btn row-action-view', title: 'View', 'aria-label': 'View', type: 'button', onClick: function (e) { e.stopPropagation(); handleView(item); } }, React.createElement(EyeIcon, null)),
                          React.createElement('button', { className: 'row-action-btn row-action-edit', title: 'Edit', 'aria-label': 'Edit', type: 'button', onClick: function (e) { e.stopPropagation(); handleEdit(item); } }, React.createElement(PencilIcon, null)),
                          React.createElement('button', { className: 'row-action-btn row-action-delete', title: 'Delete', 'aria-label': 'Delete', type: 'button', onClick: function (e) { e.stopPropagation(); setPendingDelete(item); } }, React.createElement(TrashIcon, null))
                        )
                      )
                    );
                  })
            )
          )
        ),
        filteredItems.length > 0 && React.createElement('div', { className: 'cpn-pagination' },
          React.createElement('div', { className: 'cpn-pagination-left' },
            React.createElement('span', { className: 'cpn-pagination-info' },
              'Showing ',
              React.createElement('strong', null, (filteredItems.length === 0 ? 0 : startIndex + 1) + '\u2013' + endIndex),
              ' of ',
              React.createElement('strong', null, filteredItems.length),
              ' coupons'
            ),
            React.createElement('div', { className: 'cpn-per-page-select' },
              React.createElement('span', null, 'Rows per page:'),
              React.createElement('select', {
                value: itemsPerPage,
                onChange: function (e) {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }
              },
                PER_PAGE_OPTIONS.map(function (opt) {
                  return React.createElement('option', { key: opt, value: opt }, opt);
                })
              )
            )
          ),
          React.createElement('div', { className: 'cpn-pagination-controls' },
            React.createElement('button', {
              type: 'button',
              className: 'cpn-page-btn',
              disabled: safeCurrentPage <= 1,
              onClick: function () { setCurrentPage(1); },
              title: 'First Page'
            }, '\u00AB'),
            React.createElement('button', {
              type: 'button',
              className: 'cpn-page-btn',
              disabled: safeCurrentPage <= 1,
              onClick: function () { setCurrentPage(Math.max(1, safeCurrentPage - 1)); },
              title: 'Previous Page'
            }, '\u2039'),
            getPageNumbers().map(function (p) {
              return React.createElement('button', {
                key: p,
                type: 'button',
                className: 'cpn-page-btn' + (p === safeCurrentPage ? ' active' : ''),
                onClick: function () { setCurrentPage(p); }
              }, p);
            }),
            React.createElement('button', {
              type: 'button',
              className: 'cpn-page-btn',
              disabled: safeCurrentPage >= totalPages,
              onClick: function () { setCurrentPage(Math.min(totalPages, safeCurrentPage + 1)); },
              title: 'Next Page'
            }, '\u203A'),
            React.createElement('button', {
              type: 'button',
              className: 'cpn-page-btn',
              disabled: safeCurrentPage >= totalPages,
              onClick: function () { setCurrentPage(totalPages); },
              title: 'Last Page'
            }, '\u00BB')
          )
        )
      )
    )
  );
};
export default CouponsAdmin;

